import { AlertCircle, ArrowRightLeft, X } from 'lucide-react'
import React, { useState } from 'react'
import styled from 'styled-components'
import { zeroAddress } from 'viem'

import { isAddress } from '../../utils'

const RecipientWrapper = styled.div`
  width: 100%;
  backdrop-filter: blur(4px);
  overflow: hidden;
`

const RecipientHeader = styled.div<{ isActive: boolean }>`
  width: 100%;
`

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  margin-top: 1rem;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  padding: 0;

  svg {
    opacity: 0.7;
  }

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    svg {
      opacity: 1;
    }
  }
`

const ActiveDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-top: 1rem;
`

const AddressInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`

const AddressLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  font-weight: 500;
`

const AddressValue = styled.div<{ hasError?: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ hasError, theme }) => (hasError ? '#ef4444' : theme.textPrimary)};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: inherit;
`

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`

const EditButton = styled.button`
  padding: 0.375rem 0.75rem;
  background: rgba(51, 65, 85, 0.5);
  border: 1px solid rgba(71, 85, 105, 0.6);
  border-radius: 4px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;

  &:hover {
    background: rgba(71, 85, 105, 0.5);
    color: ${({ theme }) => theme.textPrimary};
  }
`

const RemoveButton = styled.button`
  padding: 0.375rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 4px;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }
`

const InputSection = styled.div`
  border-top: 1px solid rgba(51, 65, 85, 0.3);
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-top: 1rem;
`

const AddressInput = styled.input<{ hasError: boolean }>`
  width: 100%;
  padding: 0.75rem;
  background: ${({ theme }) => theme.cardSecondary};
  border-radius: 4px;
  border: 1px solid ${({ hasError }) => (hasError ? '#ef4444' : 'rgba(71, 85, 105, 0.8)')};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;
  transition: all 0.2s ease;
  font-family: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.textSecondary};
    font-size: 14px;
    font-weight: 400;
  }

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? '#ef4444' : '#3b82f6')};
    box-shadow: 0 0 0 1px ${({ hasError }) => (hasError ? '#ef444440' : '#3b82f640')};
  }
`

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ef4444;
  font-size: 12px;
  font-weight: 500;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
`

const SaveButton = styled.button<{ disabled: boolean }>`
  flex: 1;
  padding: 0.75rem;
  background: ${({ disabled }) => (disabled ? 'rgba(71, 85, 105, 0.5)' : '#3b82f6')};
  border: 1px solid ${({ disabled }) => (disabled ? 'rgba(71, 85, 105, 0.8)' : '#3b82f6')};
  border-radius: 4px;
  color: ${({ disabled }) => (disabled ? 'rgba(148, 163, 184, 0.7)' : 'white')};
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #2563eb;
  }
`

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: transparent;
  border: 1px solid rgba(71, 85, 105, 0.8);
  border-radius: 4px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(71, 85, 105, 0.2);
    color: ${({ theme }) => theme.textPrimary};
  }
`

interface CustomRecipientProps {
  recipient: string | null
  onChangeRecipient: (recipient: string | null) => void
  account: string | undefined
}

const CustomRecipient: React.FC<CustomRecipientProps> = ({ recipient, onChangeRecipient, account }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const formatAddress = (address: string | null) => {
    if (!address) return ''
    if (address.length <= 12) return address
    return `${address.slice(0, 6)}...${address.slice(-6)}`
  }

  const validateInput = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return { isValid: true, error: null }

    if (!trimmed.startsWith('0x')) {
      return { isValid: false, error: 'Address must start with 0x' }
    }

    if (trimmed.length !== 42) {
      return { isValid: false, error: 'Address must be 42 characters' }
    }

    if (!isAddress(trimmed)) {
      return { isValid: false, error: 'Invalid address format' }
    }

    if (trimmed.toLowerCase() === zeroAddress) {
      return { isValid: false, error: 'Cannot send to zero address' }
    }

    if (trimmed.toLowerCase() === account?.toLowerCase()) {
      return { isValid: false, error: 'Cannot send to your own address' }
    }

    return { isValid: true, error: null }
  }

  const validation = validateInput(inputValue)
  const currentValidation = validateInput(recipient || '')

  const handleEdit = () => {
    setInputValue(recipient || '')
    setIsEditing(true)
  }

  const handleSave = () => {
    const trimmed = inputValue.trim()

    if (!trimmed) {
      onChangeRecipient(null)
    } else if (validation.isValid) {
      onChangeRecipient(trimmed)
    } else {
      return
    }

    setIsEditing(false)
    setInputValue('')
  }

  const handleCancel = () => {
    setIsEditing(false)
    setInputValue('')
  }

  const handleRemove = () => {
    onChangeRecipient(null)
    setIsEditing(false)
  }

  const canSave = !inputValue.trim() || validation.isValid

  const typed = inputValue.trim().length > 0

  return (
    <RecipientWrapper>
      <RecipientHeader isActive={!!recipient || isEditing}>
        {!recipient && !isEditing ? (
          <ToggleButton onClick={() => setIsEditing(true)}>
            <ArrowRightLeft size={16} />
            <span>Send to a different address</span>
          </ToggleButton>
        ) : !isEditing ? (
          <ActiveDisplay>
            <AddressInfo>
              <AddressLabel>Recipient Address</AddressLabel>
              <AddressValue hasError={!currentValidation.isValid}>
                {!currentValidation.isValid && <AlertCircle size={14} />}
                {formatAddress(recipient)}
              </AddressValue>
            </AddressInfo>
            <ActionButtons>
              <EditButton onClick={handleEdit}>Edit</EditButton>
              <RemoveButton onClick={handleRemove}>
                <X size={14} />
              </RemoveButton>
            </ActionButtons>
          </ActiveDisplay>
        ) : null}
      </RecipientHeader>

      {isEditing && (
        <InputSection>
          <InputContainer>
            <AddressInput
              type="text"
              placeholder="0x... (Enter wallet address)"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              hasError={typed && !validation.isValid}
              autoFocus
            />

            {typed && !validation.isValid && (
              <ErrorMessage>
                <AlertCircle size={14} />
                {validation.error}
              </ErrorMessage>
            )}

            <ButtonRow>
              <SaveButton disabled={!canSave} onClick={handleSave}>
                Save Address
              </SaveButton>
              <CancelButton onClick={handleCancel}>Cancel</CancelButton>
            </ButtonRow>
          </InputContainer>
        </InputSection>
      )}
    </RecipientWrapper>
  )
}

export default CustomRecipient
